import { makeObservable, observable, action } from "mobx";

// Models & Types
import { AllocationModel, IAllocationModel } from "models/allocation/entities/AllocationModel";

// Services & Helpers


import { KEYS } from "helpers/utils/StorageUtil";
import AllocationAPI from "services/AllocationAPI";
import { validateCorreiosPassword } from "utils/methods/validatesCorreiosPassword";
import LocalDBAPI from "services/LocalDBAPI";


/**
 * Store para gerenciar o estado e as operações de Alocações (pedidos).
 */
class AllocationStore {
    // --- Estado Observável ---
    allocation?: AllocationModel;
    reverses: AllocationModel[] = [];
    returns: AllocationModel[] = [];
    loading: boolean = false;

    constructor() {
        makeObservable(this, {
            allocation: observable,
            reverses: observable,
            returns: observable,
            loading: observable,
            reset: action,
            setAllocation: action,
            getAllocationByUuid: action,
            getAllocationByCodeAndLockerUuid: action,
            validateCorreiosCode: action,
            getAllocationByExternalCodeAndLockerUuid: action,
            getLockerReverseList: action,
            getLockerReturnList: action,
            create: action,
            allocate: action,
            allocationFinished: action,
            preRegisteredAllocationFinished: action,
            correiosAllocationFinished: action,
            deallocate: action,
            reverse: action,
            return: action,
            allocationReturnFinished: action,
            pickUpReturn: action,
            allocationPhoto: action,
        });
    }

    /** Reseta o estado da store. */
    reset(): void {
        this.allocation = undefined;
        this.reverses = [];
        this.returns = [];
        this.loading = false;
    }

    /** Define a alocação ativa. */
    setAllocation(allocation: AllocationModel): void {
        this.allocation = allocation;
    }

    private setLoading(value: boolean): void {
        this.loading = value;
    }

    // --- Ações Assíncronas ---

    /** Busca uma alocação pelo seu UUID. */
    async getAllocationByUuid(uuid: string): Promise<AllocationModel | undefined> {
        this.setLoading(true);
        try {
            const response = await AllocationAPI.getAllocationByUuid(uuid);
            if (!response.error) {
                const newAllocation = new AllocationModel(response);
                this.setAllocation(newAllocation);
                return newAllocation;
            }
        } catch (error) {
            console.error("Falha ao buscar alocação por UUID:", error);
        } finally {
            this.setLoading(false);
        }
    }

    /** Busca uma alocação pelo código do pedido e UUID do locker. */
    async getAllocationByCodeAndLockerUuid(code: string): Promise<AllocationModel | undefined> {
        this.setLoading(true);
        const lockerUuid = localStorage.getItem(KEYS.LOCKER_UUID);
        if (!lockerUuid) {
            console.error("UUID do Locker não encontrado no localStorage.");
            this.setLoading(false);
            return;
        }

        try {
            const response = await AllocationAPI.getAllocationByCodeAndLockerUuid(code, lockerUuid);
            if (!response.error) {
                const newAllocation = new AllocationModel(response);
                this.setAllocation(newAllocation);
                return newAllocation;
            }
        } catch (error) {
            console.error("Pedido com inconsistência no cadastro ou falha na API:", error);
        } finally {
            this.setLoading(false);
        }
    }

    /** Valida as credenciais dos Correios. */
    async validateCorreiosCode(registration: string, password: string): Promise<"OK" | undefined> {
        if (!validateCorreiosPassword(registration, password)) {
            console.error("Matrícula e/ou senha inválidas");
            return;
        }
        localStorage.setItem(KEYS.CORREIOS_SEPARATOR_REGISTRATION, registration);
        await LocalDBAPI.userLogin();
        return "OK";
    }

    /** Busca uma alocação pelo código externo e UUID do locker. */
    async getAllocationByExternalCodeAndLockerUuid(code: string): Promise<AllocationModel | undefined> {
        this.setLoading(true);
        const lockerUuid = localStorage.getItem(KEYS.LOCKER_UUID);
        if (!lockerUuid) {
            console.error("UUID do Locker não encontrado no localStorage.");
            this.setLoading(false);
            return;
        }

        try {
            const response = await AllocationAPI.getAllocationByExternalCodeAndLockerUuid(code, lockerUuid);
            if (!response.error) {
                const newAllocation = new AllocationModel(response);
                this.setAllocation(newAllocation);
                return newAllocation;
            }
        } catch (error) {
            console.error("Pedido com inconsistência no cadastro ou falha na API:", error);
        } finally {
            this.setLoading(false);
        }
    }

    /** Busca a lista de reversas pendentes no locker. */
    async getLockerReverseList(): Promise<AllocationModel[] | undefined> {
        this.setLoading(true);
        const lockerUuid = localStorage.getItem(KEYS.LOCKER_UUID);
        if (!lockerUuid) {
            this.setLoading(false);
            return;
        }

        try {
            const response = await AllocationAPI.getLockerReverseList(lockerUuid);
            if (!response.error) {
                this.reverses = response.content.map((i: IAllocationModel) => new AllocationModel(i));
                return this.reverses;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Busca a lista de devoluções pendentes no locker. */
    async getLockerReturnList(): Promise<AllocationModel[] | undefined> {
        this.setLoading(true);
        const lockerUuid = localStorage.getItem(KEYS.LOCKER_UUID);
        if (!lockerUuid) {
            this.setLoading(false);
            return;
        }

        try {
            const response = await AllocationAPI.getLockerReturnList(lockerUuid);
            if (!response.error) {
                this.returns = response.content.map((i: IAllocationModel) => new AllocationModel(i));
                return this.returns;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Cria uma nova alocação. */
    async create(data: Partial<IAllocationModel>): Promise<AllocationModel | undefined> {
        this.setLoading(true);
        delete data['containerAllocations'];
        try {
            const response = await AllocationAPI.create(data);
            if (!response.error) {
                const newAllocation = new AllocationModel(response);
                this.setAllocation(newAllocation);
                return newAllocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Aloca um item em um compartimento. */
    async allocate(data: any): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.allocate(this.allocation.uuid, data);
            if (!response.error) {
                const updatedAllocation = new AllocationModel(response);
                this.setAllocation(updatedAllocation);
                return updatedAllocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Finaliza uma alocação. */
    async allocationFinished(): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.allocationFinished(this.allocation.uuid);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Finaliza alocação para fluxo de alocação pre-cadastrada. */
    async preRegisteredAllocationFinished(): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.preRegisteredAllocationFinished(this.allocation.uuid,);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Finaliza alocação para fluxo dos correios. */
    async correiosAllocationFinished(): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.correiosAllocationFinished(this.allocation.uuid,);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Desaloca um item de um compartimento. */
    async deallocate(data: any): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.deallocate(this.allocation.uuid, data);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                if (data.containerAllocations?.[0]?.container?.uuid) {
                    LocalDBAPI.retrieveOrder(data.containerAllocations[0].container.uuid, this.allocation.uuid!);
                }
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Realiza a reversa de um item. */
    async reverse(data: any): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.reverse(this.allocation.uuid, data);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Realiza a devolução de um item. */
    async return(data: any): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.return(this.allocation.uuid, data);
            if (!response.error) {
                const newAllocation = new AllocationModel(response);
                this.setAllocation(newAllocation);
                return newAllocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Finaliza devolução de alocação. */
    async allocationReturnFinished(): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.allocationReturnFinished(this.allocation.uuid,);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Realiza retirada de devolução em porta. */
    async pickUpReturn(data: any): Promise<AllocationModel | undefined> {
        if (!this.allocation?.uuid) return;
        this.setLoading(true);
        try {
            const response = await AllocationAPI.pickUpReturn(this.allocation.uuid, data);
            if (!response.error) {
                this.allocation = new AllocationModel(response);
                return this.allocation;
            }
        } finally {
            this.setLoading(false);
        }
    }

    /** Envia a foto de uma alocação. */
    async allocationPhoto(uuid: string, photo: Blob): Promise<boolean> {
        this.setLoading(true);
        try {
            const data = new FormData();
            const file = new File([photo], `${uuid}.jpg`, { type: "image/jpeg" });
            data.append("photo", file);
            const response = await AllocationAPI.allocationPhoto(uuid, data);
            return !response.error;
        } catch (error) {
            console.error("Falha ao enviar foto da alocação:", error);
            return false;
        } finally {
            this.setLoading(false);
        }
    }
}

export default AllocationStore;